import React, { useEffect, useRef, useState } from 'react';
import { FiX } from 'react-icons/fi';
import './SimpleColorPicker.scss';

const defaultColors = [
  '#FF6900', '#FCB900', '#7BDCB5', '#00D084', '#8ED1FC',
  '#0693E3', '#ABB8C3', '#EB144C', '#F78DA7', '#9900EF'
];

const normalizeColor = (value) => value.toUpperCase();

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 255, g: 255, b: 255 };
};

const rgbToHex = (r, g, b) => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

const rgbToHsv = (r, g, b) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  const s = max === 0 ? 0 : d / max;
  const v = max;

  if (d !== 0) {
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    v: Math.round(v * 100)
  };
};

const hsvToRgb = (h, s, v) => {
  s /= 100;
  v /= 100;
  const hue = ((h % 360) + 360) % 360;
  const c = v * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = v - c;
  let r = 0;
  let g = 0;
  let b = 0;

  if (hue < 60) { r = c; g = x; }
  else if (hue < 120) { r = x; g = c; }
  else if (hue < 180) { g = c; b = x; }
  else if (hue < 240) { g = x; b = c; }
  else if (hue < 300) { r = x; b = c; }
  else { r = c; b = x; }

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255)
  };
};

const normalizeHex = (color) => {
  const hex = color.startsWith('#') ? color : `#${color}`;
  return normalizeColor(hex);
};

const hsvFromHex = (hex, previous = { h: 0, s: 0, v: 100 }) => {
  const rgb = hexToRgb(hex);
  const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
  return {
    rgb,
    hsv: {
      h: hsv.s === 0 ? previous.h : hsv.h,
      s: hsv.v === 0 ? previous.s : hsv.s,
      v: hsv.v
    }
  };
};

const getPointerRatio = (event, element) => {
  const rect = element.getBoundingClientRect();
  const style = window.getComputedStyle(element);
  const borderLeft = parseFloat(style.borderLeftWidth) || 0;
  const borderRight = parseFloat(style.borderRightWidth) || 0;
  const borderTop = parseFloat(style.borderTopWidth) || 0;
  const borderBottom = parseFloat(style.borderBottomWidth) || 0;
  const width = Math.max(rect.width - borderLeft - borderRight, 1);
  const height = Math.max(rect.height - borderTop - borderBottom, 1);
  const x = Math.max(0, Math.min(width, event.clientX - rect.left - borderLeft));
  const y = Math.max(0, Math.min(height, event.clientY - rect.top - borderTop));
  return { x: x / width, y: y / height };
};

const SimpleColorPicker = ({ color = '#ffffff', onChange, onClose, colors }) => {
  const initial = hsvFromHex(normalizeHex(color));
  const [hexValue, setHexValue] = useState(normalizeHex(color).replace('#', ''));
  const [rgbValues, setRgbValues] = useState(initial.rgb);
  const [hsvValues, setHsvValues] = useState(initial.hsv);
  const [isDragging, setIsDragging] = useState(false);

  const hsvRef = useRef(initial.hsv);
  const lastEmittedHexRef = useRef(normalizeHex(color));
  const dragRef = useRef({ active: false, type: null, element: null });

  const displayColors = colors || defaultColors;

  const isColorSelected = (colorHex) => {
    return normalizeColor(color) === normalizeColor(colorHex);
  };

  const commitHsv = (h, s, v, shouldClose = false) => {
    const nextHsv = { h, s, v };
    const rgb = hsvToRgb(h, s, v);
    const hex = normalizeColor(rgbToHex(rgb.r, rgb.g, rgb.b));

    hsvRef.current = nextHsv;
    lastEmittedHexRef.current = hex;
    setHsvValues(nextHsv);
    setRgbValues(rgb);
    setHexValue(hex.replace('#', ''));
    onChange && onChange(hex, shouldClose);
  };

  const commitHex = (hex, shouldClose = false) => {
    const normalizedHex = normalizeHex(hex);
    const { rgb, hsv } = hsvFromHex(normalizedHex, hsvRef.current);

    hsvRef.current = hsv;
    lastEmittedHexRef.current = normalizedHex;
    setHsvValues(hsv);
    setRgbValues(rgb);
    setHexValue(normalizedHex.replace('#', ''));
    onChange && onChange(normalizedHex, shouldClose);
  };

  useEffect(() => {
    const hex = normalizeHex(color);
    if (hex === lastEmittedHexRef.current) return;
    const { rgb, hsv } = hsvFromHex(hex, hsvRef.current);
    hsvRef.current = hsv;
    lastEmittedHexRef.current = hex;
    setHexValue(hex.replace('#', ''));
    setRgbValues(rgb);
    setHsvValues(hsv);
  }, [color]);

  const handleHexChange = (e) => {
    const value = e.target.value.toUpperCase();
    setHexValue(value);

    if (value.length === 6 && /^[0-9A-F]{6}$/i.test(value)) {
      commitHex(`#${value}`, false);
    }
  };

  const handleRgbChange = (channel, value) => {
    const numValue = parseInt(value, 10) || 0;
    const clampedValue = Math.max(0, Math.min(255, numValue));
    const newRgb = { ...rgbValues, [channel]: clampedValue };
    commitHex(rgbToHex(newRgb.r, newRgb.g, newRgb.b), false);
  };

  const handleColorClick = (hexColor) => {
    commitHex(hexColor, true);
  };

  const updateFromSaturationValue = (event, element) => {
    const target = element || dragRef.current.element;
    if (!target) return;
    const { x, y } = getPointerRatio(event, target);
    const s = Math.round(x * 100);
    const v = Math.round((1 - y) * 100);
    commitHsv(hsvRef.current.h, s, v);
  };

  const updateFromHue = (event, element) => {
    const target = element || dragRef.current.element;
    if (!target) return;
    const { x } = getPointerRatio(event, target);
    const h = Math.round(x * 360);
    commitHsv(h, hsvRef.current.s, hsvRef.current.v);
  };

  const handleSaturationValuePointerDown = (e) => {
    e.preventDefault();
    if (e.currentTarget.setPointerCapture && e.pointerId != null) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    dragRef.current = { active: true, type: 'sv', element: e.currentTarget };
    setIsDragging(true);
    updateFromSaturationValue(e, e.currentTarget);
  };

  const handleHuePointerDown = (e) => {
    e.preventDefault();
    if (e.currentTarget.setPointerCapture && e.pointerId != null) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    dragRef.current = { active: true, type: 'hue', element: e.currentTarget };
    setIsDragging(true);
    updateFromHue(e, e.currentTarget);
  };

  const handleDragPointerMove = (e) => {
    if (!dragRef.current.active) return;
    if (dragRef.current.type === 'sv') {
      updateFromSaturationValue(e);
    } else if (dragRef.current.type === 'hue') {
      updateFromHue(e);
    }
  };

  const handleDragPointerUp = (e) => {
    if (e.currentTarget.releasePointerCapture && e.pointerId != null) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // Capture may already be released
      }
    }
    dragRef.current = { active: false, type: null, element: null };
    setIsDragging(false);

    const suppressClick = (event) => {
      event.stopPropagation();
    };
    document.addEventListener('click', suppressClick, true);
    setTimeout(() => {
      document.removeEventListener('click', suppressClick, true);
    }, 0);
  };

  return (
    <div className={`simple-color-picker${isDragging ? ' is-dragging' : ''}`}>
      <div className="picker-header">
        <span>Seleccionar color</span>
        <button type="button" className="btn-close" onClick={onClose}>
          <FiX />
        </button>
      </div>

      <div className="picker-content">
        <div className="visual-picker">
          <div
            className="saturation-lightness-area"
            style={{
              background: `linear-gradient(to right, #fff, hsl(${hsvValues.h}, 100%, 50%)), linear-gradient(to bottom, transparent, #000)`
            }}
            onPointerDown={handleSaturationValuePointerDown}
            onPointerMove={handleDragPointerMove}
            onPointerUp={handleDragPointerUp}
            onPointerCancel={handleDragPointerUp}
          >
            <div
              className="saturation-lightness-selector"
              style={{
                left: `${hsvValues.s}%`,
                top: `${100 - hsvValues.v}%`,
                backgroundColor: `rgb(${rgbValues.r}, ${rgbValues.g}, ${rgbValues.b})`
              }}
            />
          </div>

          <div
            className="hue-slider"
            style={{ background: 'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)' }}
            onPointerDown={handleHuePointerDown}
            onPointerMove={handleDragPointerMove}
            onPointerUp={handleDragPointerUp}
            onPointerCancel={handleDragPointerUp}
          >
            <div
              className="hue-selector"
              style={{ left: `${(hsvValues.h / 360) * 100}%` }}
            />
          </div>
        </div>

        <div className="color-swatches">
          {displayColors.map((colorHex, index) => (
            <button
              key={index}
              type="button"
              className={`color-swatch ${isColorSelected(colorHex) ? 'selected' : ''}`}
              style={{ backgroundColor: colorHex }}
              onClick={() => handleColorClick(colorHex)}
              title={colorHex}
            />
          ))}
        </div>

        <div className="hex-input-section">
          <label>Hexadecimal</label>
          <div className="hex-input-container">
            <span className="hex-prefix">#</span>
            <input
              type="text"
              value={hexValue}
              onChange={handleHexChange}
              placeholder="FFFFFF"
              maxLength="6"
              className="hex-input"
            />
          </div>
        </div>

        <div className="rgb-section">
          <label>RGB</label>
          <div className="rgb-inputs">
            <div className="rgb-input-group">
              <span>R</span>
              <input
                type="number"
                min="0"
                max="255"
                value={rgbValues.r}
                onChange={(e) => handleRgbChange('r', e.target.value)}
                className="rgb-input"
              />
            </div>
            <div className="rgb-input-group">
              <span>G</span>
              <input
                type="number"
                min="0"
                max="255"
                value={rgbValues.g}
                onChange={(e) => handleRgbChange('g', e.target.value)}
                className="rgb-input"
              />
            </div>
            <div className="rgb-input-group">
              <span>B</span>
              <input
                type="number"
                min="0"
                max="255"
                value={rgbValues.b}
                onChange={(e) => handleRgbChange('b', e.target.value)}
                className="rgb-input"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleColorPicker;
